"use client";

import { ChevronLeft } from "lucide-react";
import Link from "next/link";
import { useTheme } from "next-themes";
import type React from "react";
import { useEffect, useId, useState } from "react";
import { useSidebarCollapse } from "../default/sidebar/context/SidebarCollapseContext";
import { Button } from "../tailwind/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tailwind/ui/tooltip";
import { VaraAnimatedHeader } from "./ui/VaraAnimatedHeader";
import { ScribaIcon } from "./Icon";

interface SidebarHeaderProps {
  showCollapseButton?: boolean;
}

export const SidebarHeader: React.FC<SidebarHeaderProps> = ({ showCollapseButton = true }) => {
  const { isCollapsed, toggleCollapsed } = useSidebarCollapse();
  const { theme } = useTheme();
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const headerId = useId();

  // Start animation after sidebar transition completes using transitionend event
  useEffect(() => {
    if (!isCollapsed) {
      const handleTransitionEnd = (event: TransitionEvent) => {
        // Only respond to width transitions on the sidebar
        if (event.propertyName === "width") {
          setShouldAnimate(true);
        }
      };

      // Find the sidebar element and listen for transitionend
      const sidebarElement = document.querySelector("[data-sidebar]");
      if (sidebarElement) {
        sidebarElement.addEventListener("transitionend", handleTransitionEnd);

        // Also set a fallback timer in case transitionend doesn't fire
        // This handles the case where sidebar is already open on page load
        const fallbackTimer = setTimeout(() => {
          setShouldAnimate(true);
        }, 100); // Small delay to ensure DOM is ready

        return () => {
          sidebarElement.removeEventListener("transitionend", handleTransitionEnd);
          clearTimeout(fallbackTimer);
        };
      } else {
        // Fallback: use a timeout if we can't find the element
        const timer = setTimeout(() => setShouldAnimate(true), 350);
        return () => clearTimeout(timer);
      }
    } else {
      setShouldAnimate(false);
    }
  }, [isCollapsed]);

  return (
    <div className="flex items-center h-12 relative w-full">
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            href="/"
            className="flex items-center w-full h-full p-4 hover:bg-gray-200 dark:hover:bg-muted transition-colors"
          >
            {/* Logo/Icon */}
            <div className={`flex items-center w-8 flex-shrink-0 ${isCollapsed ? "justify-center" : "justify-start"}`}>
              <ScribaIcon className="w-6 h-6 text-gray-900 dark:text-white" />
            </div>

            {/* Title - Only show when animation should start (after sidebar transition completes) */}
            {!isCollapsed && shouldAnimate && (
              <div className="flex items-center pl-2">
                <VaraAnimatedHeader
                  id={`vara-sidebar-header-${headerId}`}
                  text="Scriba"
                  fontSize={20}
                  color={theme === "dark" ? "#ffffff" : "#111827"}
                  duration={1500}
                  textAlign="left"
                  minHeight={24}
                  className="[&>svg]:!ml-0 [&>svg]:!block [&>svg]:translate-y-1"
                  replaceOnResize={false}
                />
              </div>
            )}

            {/* Invisible placeholder to maintain layout while animation hasn't started */}
            {!isCollapsed && !shouldAnimate && (
              <div className="flex items-center pl-2 min-h-[24px]">
                <span className="text-lg font-medium text-gray-900 dark:text-white opacity-0">Scriba</span>
              </div>
            )}
          </Link>
        </TooltipTrigger>
        {isCollapsed && (
          <TooltipContent side="right" className="ml-2 z-[10000]">
            <p>Home</p>
          </TooltipContent>
        )}
      </Tooltip>

      {/* Collapse Button - Only show when not collapsed and enabled */}
      {!isCollapsed && showCollapseButton && (
        <Button
          variant="ghost"
          onClick={toggleCollapsed}
          className="absolute right-2 flex-shrink-0 p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-muted transition-colors"
          title="Collapse Sidebar"
        >
          <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
        </Button>
      )}
    </div>
  );
};
